import { useRef } from "react";
import { pitchClass } from "../../theory/notes";

const BLACK = new Set([1, 3, 6, 8, 10]);

export interface KeyboardProps {
  startNote: number; // should be a C
  octaves: number;
  highlight: ReadonlySet<number>; // pitch classes to mark (e.g. scale)
  tonicPc: number;
  held: ReadonlySet<number>;
  nameOf: (midi: number) => string;
  keyHints?: ReadonlyMap<number, string>; // midi → computer key label
  onNoteOn: (note: number) => void;
  onNoteOff: (note: number) => void;
}

/** On-screen piano. Pointer, touch and keyboard (Enter/Space on a focused key) input. */
export function Keyboard(p: KeyboardProps) {
  const pointerNotes = useRef(new Map<number, number>()); // pointerId → note
  const notes = Array.from({ length: p.octaves * 12 + 1 }, (_, i) => p.startNote + i);
  const whites = notes.filter((n) => !BLACK.has(pitchClass(n)));
  const whiteWidth = 100 / whites.length;

  const down = (note: number, pointerId: number) => {
    const prev = pointerNotes.current.get(pointerId);
    if (prev === note) return;
    if (prev !== undefined) p.onNoteOff(prev);
    pointerNotes.current.set(pointerId, note);
    p.onNoteOn(note);
  };
  const up = (pointerId: number) => {
    const n = pointerNotes.current.get(pointerId);
    if (n === undefined) return;
    pointerNotes.current.delete(pointerId);
    p.onNoteOff(n);
  };

  const keyEl = (note: number) => {
    const pc = pitchClass(note);
    const black = BLACK.has(pc);
    const whiteIndex = whites.filter((w) => w < note).length;
    const style = black ? { left: `${whiteIndex * whiteWidth - whiteWidth * 0.3}%`, width: `${whiteWidth * 0.6}%` } : { width: `${whiteWidth}%` };
    const name = p.nameOf(note);
    const hint = p.keyHints?.get(note);
    const classes = ["key", black ? "black" : "white", p.highlight.has(pc) ? "in-scale" : "", pc === p.tonicPc ? "tonic" : "", p.held.has(note) ? "held" : ""].join(" ");
    return (
      <button
        key={note} type="button" className={classes} style={style}
        aria-label={`${name}${p.highlight.has(pc) ? ", in scale" : ""}`} aria-pressed={p.held.has(note)}
        onPointerDown={(e) => { e.preventDefault(); e.currentTarget.releasePointerCapture?.(e.pointerId); down(note, e.pointerId); }}
        onPointerEnter={(e) => { if (e.buttons === 1 && pointerNotes.current.has(e.pointerId)) down(note, e.pointerId); }}
        onPointerUp={(e) => up(e.pointerId)}
        onPointerCancel={(e) => up(e.pointerId)}
        onPointerLeave={(e) => { if (e.buttons === 0) up(e.pointerId); }}
        onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !e.repeat) { e.preventDefault(); p.onNoteOn(note); } }}
        onKeyUp={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); p.onNoteOff(note); } }}
        onBlur={() => { if (p.held.has(note)) p.onNoteOff(note); }}
      >
        <span className="key-label" aria-hidden="true">{!black && (pc === 0 || p.highlight.has(pc)) ? name : ""}</span>
        {hint && <span className="key-hint" aria-hidden="true">{hint}</span>}
      </button>
    );
  };

  return (
    <div className="keyboard" role="group" aria-label="Piano keyboard" onPointerUp={() => { for (const id of [...pointerNotes.current.keys()]) up(id); }}>
      <div className="keys-white">{whites.map(keyEl)}</div>
      <div className="keys-black">{notes.filter((n) => BLACK.has(pitchClass(n))).map(keyEl)}</div>
    </div>
  );
}
